/**
 * DsoTile — Kachel „Ø Zahlungsdauer" (DSO) fürs Owner-Cockpit
 * (M-CFO-4, `architecture-cockpit-cfo.md` §8.4).
 *
 * Zeigt die durchschnittliche Zahlungsdauer als Kennzahl mit Trendpfeil und
 * Ampel (grün/gelb/rot). **Fail-safe:** bei zu wenigen erfassten Zahlungen
 * ({@link computeDso} liefert `insufficient`) rendert die Kachel „—" statt
 * einer irreführenden Zahl und bietet — sofern das Bank-Abgleich-Gate offen ist —
 * die Synergie-CTA „Bank-Abgleich starten" (der Kontoumsatz-Abgleich füllt genau
 * diese Datenlücke).
 *
 * Props-getrieben: konsumiert die auf CockpitPage-Ebene bereits geladenen
 * {@link CockpitOpenItemRow}[] (0 neue Firestore-Reads). Bei einem gebubbelten
 * Ladefehler zeigt die Kachel einen isolierten Fehler-Zustand mit eigenem Retry
 * (Muster `OpenItemsAgingChartTile`) — nie ein Render-Throw.
 *
 * A11y: Ampel und Trend werden NIE nur über Farbe kommuniziert (Icon + Text,
 * WCAG 1.4.1); der Trendpfeil trägt ein ausgeschriebenes `aria-label`.
 *
 * @module components/cockpit/DsoTile
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { IconHourglass, IconCircle, IconArrowUp, IconArrowDown, IconTrendFlat, type FbIcon } from '../../icons';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { Spinner } from '../common/Spinner';
import logger from '../../utils/logger';
import { computeDso, type DsoResult } from '../../utils/cockpitReceivables';
import { loadOpenItemRows, type CockpitOpenItemRow } from '../../loaders/cockpitLoaders';
import { canUseBankReconcile } from '../../config/bankReconcileAccess';
import type { Tier } from '../../config/entitlements';

/** Today's date as ISO `YYYY-MM-DD` (local) — bewusst dupliziert (Muster `CockpitPage`). */
function todayISOdefault(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Ampel-Zone → Tailwind-Textfarbe (real green/amber/red; A11y stets mit Icon+Text kombiniert). */
const LIGHT_COLOR: Record<'green' | 'yellow' | 'red', string> = {
  green: 'text-[var(--tone-success-fg)]',
  yellow: 'text-[var(--tone-warning-fg)]',
  red: 'text-[var(--tone-danger-fg)]',
};

export interface DsoTileProps {
  /** Effektiver Scope-UID — für den isolierten Retry (`loadOpenItemRows`). */
  scopeUid: string;
  /** Nutzer-UID für das Bank-Abgleich-Gate (`canUseBankReconcile`). */
  uid: string | undefined;
  /** Effektiver Tier für das Bank-Abgleich-Gate (Ultra, §3.2). */
  tier: Tier;
  /** Gebubbelte offene-Posten-Zeilen; `null` ⇒ noch am Laden. */
  rows: CockpitOpenItemRow[] | null;
  /** Gebubbelter Ladefehler der offenen Posten. */
  rowsError: boolean;
  /** Referenzdatum (Test-Injektion); Default = heute. */
  todayISO?: string;
}

type LocalState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; data: CockpitOpenItemRow[] };

/**
 * @param props - {@link DsoTileProps}.
 */
const DsoTile: React.FC<DsoTileProps> = ({ scopeUid, uid, tier, rows, rowsError, todayISO }) => {
  const { t } = useTranslation('cockpit');
  const navigate = useNavigate();
  const [manual, setManual] = useState<LocalState | null>(null);
  const today = todayISO ?? todayISOdefault();

  const state: LocalState =
    manual ??
    (rowsError
      ? { status: 'error' }
      : rows !== null
        ? { status: 'ready', data: rows }
        : { status: 'loading' });

  const retry = async (): Promise<void> => {
    setManual({ status: 'loading' });
    try {
      const fresh = await loadOpenItemRows(scopeUid);
      setManual({ status: 'ready', data: fresh });
    } catch (err) {
      setManual({ status: 'error' });
      logger.error('CockpitPage: DsoTile-Retry (offene Posten) fehlgeschlagen', err);
    }
  };

  return (
    <Card>
      <div className="flex items-center gap-2 mb-2 text-secondary">
        <IconHourglass aria-hidden={true} />
        <h3 className="text-sm font-semibold">{t('cfo.dso.title')}</h3>
      </div>

      {state.status === 'loading' && (
        <div className="flex justify-center py-2">
          <Spinner size="sm" />
        </div>
      )}

      {state.status === 'error' && (
        <div className="flex items-center justify-between gap-2">
          <span className="text-secondary">{t('empty.dash')}</span>
          <Button size="sm" variant="secondary" onClick={retry}>
            {t('retry')}
          </Button>
        </div>
      )}

      {state.status === 'ready' && (
        <DsoReady result={computeDso(state.data, today)} uid={uid} tier={tier} navigate={navigate} t={t} />
      )}
    </Card>
  );
};

/** Reiner Präsentations-Teil für den `ready`-Zustand (Ampel/Trend bzw. Fail-safe). */
const DsoReady: React.FC<{
  result: DsoResult;
  uid: string | undefined;
  tier: Tier;
  navigate: (to: string) => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}> = ({ result, uid, tier, navigate, t }) => {
  if (result.kind === 'insufficient') {
    const bankGateOpen = canUseBankReconcile(uid, tier);
    return (
      <div>
        <p className="text-2xl font-bold text-primary">{t('empty.dash')}</p>
        <p className="text-sm text-secondary mt-1">{t('cfo.dso.insufficient')}</p>
        {bankGateOpen && (
          <div className="mt-2">
            <Button size="sm" variant="secondary" onClick={() => navigate('/dashboard/bank-abgleich')}>
              {t('cfo.dso.bankCta')}
            </Button>
          </div>
        )}
        <p className="text-xs text-muted mt-2">{t('cfo.dso.basedOnPayments')}</p>
      </div>
    );
  }

  const lightLabel = t(`cfo.dso.light.${result.light}`);
  return (
    <div>
      <p className="text-2xl font-bold text-primary">{t('cfo.dso.days', { count: result.dsoDays })}</p>
      <div className="flex items-center gap-2 mt-1 flex-wrap">
        <span className="inline-flex items-center gap-1 text-xs font-medium">
          <IconCircle aria-hidden={true} size={10} className={LIGHT_COLOR[result.light]} />
          <span className={LIGHT_COLOR[result.light]}>{lightLabel}</span>
        </span>
        {result.trend && <DsoTrend trend={result.trend} t={t} />}
      </div>
      <p className="text-xs text-muted mt-2">{t('cfo.dso.basedOnPayments')}</p>
    </div>
  );
};

/** Trendpfeil mit ausgeschriebenem `aria-label` (nie nur Farbe/Icon). */
const DsoTrend: React.FC<{
  trend: { prevDsoDays: number; direction: 'up' | 'down' | 'flat' };
  t: (key: string, opts?: Record<string, unknown>) => string;
}> = ({ trend, t }) => {
  const Icon: FbIcon = trend.direction === 'up' ? IconArrowUp : trend.direction === 'down' ? IconArrowDown : IconTrendFlat;
  const directionWord = t(`cfo.dso.trend.${trend.direction}`);
  const label = t('cfo.dso.trendAria', { direction: directionWord, days: trend.prevDsoDays });
  return (
    <span className="inline-flex items-center gap-1 text-xs text-secondary" aria-label={label}>
      <Icon aria-hidden={true} />
      <span aria-hidden="true">{t('cfo.dso.trendFrom', { days: trend.prevDsoDays })}</span>
    </span>
  );
};

export default DsoTile;
